import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaPlus, FaSearch, FaList, FaTh, FaCalendarAlt, FaMapMarkerAlt, FaPhone, FaUser, FaEye } from 'react-icons/fa';
import { BiPencil } from 'react-icons/bi';
import client from '../../api/client';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import PatientRegistrationFormFields from '../../components/patient/PatientRegistrationFormFields';

const EMPTY_FORM = {
  fullName: '',
  age: '',
  gender: '',
  address: '',
  landmark: '',
  pincode: '',
  emergencyName: '',
  emergencyPhone: '',
  allergies: '',
  medicalHistory: '',
  currentMedications: '',
  mobileNumber: '',
  emailId: '',
  password: '',
  reenterPassword: '',
};

const PLACEHOLDER_VALUES = new Set(['none', 'n/a', 'na', '-', '—']);

function displayValue(value) {
  if (value == null) return '—';
  const t = String(value).trim();
  if (!t || PLACEHOLDER_VALUES.has(t.toLowerCase())) return '—';
  return t;
}

function AdminPatients() {
  const { user } = useAuth();
  const adminUserId = user?.userId || 1;

  const [patients, setPatients] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');

  const [showViewModal, setShowViewModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [locationName, setLocationName] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);
  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('table');

  const passwordsMatch =
    formData.password === formData.reenterPassword && formData.reenterPassword !== '';

  const resetFormErrors = () => {
    setPincodeError('');
    setMobileError('');
    setEmailError('');
  };

  const loadDistricts = useCallback(async () => {
    try {
      const res = await client.get('/api/districts');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setDistricts(res.data.data);
      }
    } catch {
      /* optional */
    }
  }, []);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get('/api/admin/patients', {
        params: {
          search: searchTerm || undefined,
          districtId: districtFilter || undefined,
          gender: genderFilter || undefined,
        },
      });
      if (res.data?.success) {
        setPatients(res.data.data || []);
      } else {
        toast.error(res.data?.message || 'Unable to load patients.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load patients.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, districtFilter, genderFilter]);

  useEffect(() => {
    loadDistricts();
  }, [loadDistricts]);

  useEffect(() => {
    const timer = setTimeout(loadPatients, 300);
    return () => clearTimeout(timer);
  }, [loadPatients]);

  const validatePincode = async () => {
    let message = '';
    setPincodeError('');
    setLocationName('');
    setIdDistrict(null);

    if (formData.pincode.length !== 6) {
      message = 'Enter a 6-digit pincode.';
      setPincodeError(message);
      return { ok: false, message };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(
        `/api/districts/validate-pincode?pincode=${formData.pincode}`,
      );
      const result = response.data?.data;

      if (result?.districtName) {
        setLocationName(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        return { ok: true, message: '' };
      }

      message = 'Enter a valid pincode.';
      setPincodeError(message);
      return { ok: false, message };
    } catch {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  const validateContact = async (field = 'both') => {
    let message = '';
    if (field !== 'email') setMobileError('');
    if (field !== 'mobile') setEmailError('');

    const emailValid = field === 'mobile' ? true : /\S+@\S+\.\S+/.test(formData.emailId);
    const mobileValid =
      field === 'email' ? true : /^[6-9]\d{9}$/.test(formData.mobileNumber);

    if (!emailValid || !mobileValid) {
      if (!emailValid) {
        setEmailError('Enter a valid email.');
        message = 'Enter a valid email.';
      }
      if (!mobileValid) {
        setMobileError('Enter a valid 10-digit mobile number.');
        message = message || 'Enter a valid 10-digit mobile number.';
      }
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : formData.emailId || null,
        mobileNumber: field === 'email' ? null : formData.mobileNumber || null,
        excludePatientID: isEditMode && selectedPatient ? selectedPatient.idPatient : null,
      };
      const response = await client.post('/api/users/validate-contact', payload);
      const exists = response.data?.data;

      if (exists) {
        if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
        }
        return { ok: false, message };
      }

      return { ok: true, message: '' };
    } catch {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'email') setEmailError(message);
      else if (field === 'mobile') setMobileError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'password' || name === 'reenterPassword') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (name === 'pincode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, pincode: numericValue }));
      setPincodeError('');
      setLocationName('');
      setIdDistrict(null);
      return;
    }

    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, mobileNumber: numericValue }));
      setMobileError('');
      return;
    }

    if (name === 'emergencyPhone') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, emergencyPhone: numericValue }));
      return;
    }

    if (name === 'age') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 3);
      setFormData((prev) => ({ ...prev, age: numericValue }));
      return;
    }

    if (name === 'emailId') {
      setFormData((prev) => ({ ...prev, emailId: value }));
      setEmailError('');
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAdd = () => {
    setIsEditMode(false);
    setSelectedPatient(null);
    setFormData(EMPTY_FORM);
    setLocationName('');
    setIdDistrict(null);
    setShowPassword(false);
    setShowReenterPassword(false);
    resetFormErrors();
    setShowFormModal(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await client.get(`/api/patients/${row.idPatient}`);
      if (!res.data?.success) {
        toast.error('Unable to load patient.');
        return;
      }
      const p = res.data.data;
      setIsEditMode(true);
      setSelectedPatient(p);
      setFormData({
        fullName: p.fullName || '',
        age: String(p.age || ''),
        gender: p.gender || '',
        address: p.addressLine || '',
        landmark: p.landmark || '',
        pincode: p.pinCode || '',
        emergencyName:
          p.emergencyContactName && !PLACEHOLDER_VALUES.has(p.emergencyContactName.toLowerCase())
            ? p.emergencyContactName
            : '',
        emergencyPhone:
          p.emergencyContactPhoneNumber &&
          !PLACEHOLDER_VALUES.has(p.emergencyContactPhoneNumber.toLowerCase())
            ? p.emergencyContactPhoneNumber
            : '',
        allergies:
          p.knownAllergies && !PLACEHOLDER_VALUES.has(p.knownAllergies.toLowerCase())
            ? p.knownAllergies
            : '',
        medicalHistory:
          p.medicalHistory && !PLACEHOLDER_VALUES.has(p.medicalHistory.toLowerCase())
            ? p.medicalHistory
            : '',
        currentMedications:
          p.currentMedications && !PLACEHOLDER_VALUES.has(p.currentMedications.toLowerCase())
            ? p.currentMedications
            : '',
        mobileNumber: p.mobileNumber || '',
        emailId: p.email || '',
        password: '',
        reenterPassword: '',
      });
      setLocationName(p.districtName || '');
      setIdDistrict(p.districtID || null);
      resetFormErrors();
      setShowFormModal(true);
    } catch {
      toast.error('Unable to load patient.');
    }
  };

  const openView = async (row) => {
    try {
      const res = await client.get(`/api/patients/${row.idPatient}`);
      if (res.data?.success) {
        setSelectedPatient(res.data.data);
        setShowViewModal(true);
      }
    } catch {
      toast.error('Unable to load patient.');
    }
  };

  const validateForm = () => {
    if (formData.fullName.trim().length < 3) {
      toast.error('Please enter full name (min 3 characters).');
      return false;
    }
    const age = parseInt(formData.age, 10);
    if (!age || age < 1 || age > 120) {
      toast.error('Please enter a valid age (1–120).');
      return false;
    }
    if (!formData.gender) {
      toast.error('Please select gender.');
      return false;
    }
    if (formData.address.trim().length < 5) {
      toast.error('Please enter address.');
      return false;
    }
    if (formData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode.');
      return false;
    }
    if (formData.emergencyPhone && !/^[6-9]\d{9}$/.test(formData.emergencyPhone)) {
      toast.error('Please enter a valid 10-digit emergency contact number.');
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
      toast.error('Please enter a valid mobile number.');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.emailId)) {
      toast.error('Please enter a valid email.');
      return false;
    }
    if (!isEditMode) {
      if (formData.password !== formData.reenterPassword) {
        toast.error('Passwords do not match.');
        return false;
      }
      if (formData.password.length < 6 || formData.password.length > 20) {
        toast.error('Password must be between 6 and 20 characters.');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) return;

    const pincodeResult = await validatePincode();
    if (!pincodeResult.ok) {
      toast.error(pincodeResult.message || 'Please enter a valid pincode.');
      return;
    }

    if (!isEditMode) {
      const [mobileResult, emailResult] = await Promise.all([
        validateContact('mobile'),
        validateContact('email'),
      ]);
      if (!mobileResult.ok) {
        toast.error(mobileResult.message || 'Please enter a valid mobile number.');
        return;
      }
      if (!emailResult.ok) {
        toast.error(emailResult.message || 'Please enter a valid email.');
        return;
      }
    }
    if (!locationName) {
      toast.error('Enter a valid pincode to fetch location.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode && selectedPatient) {
        const payload = {
          idPatient: selectedPatient.idPatient,
          fullName: formData.fullName.trim(),
          age: parseInt(formData.age, 10),
          gender: formData.gender,
          mobileNumber: formData.mobileNumber.trim(),
          email: formData.emailId.trim(),
          addressLine: formData.address.trim(),
          landmark: formData.landmark.trim() || null,
          districtID: idDistrict,
          pinCode: formData.pincode,
          emergencyContactName: formData.emergencyName.trim() || 'N/A',
          emergencyContactPhoneNumber: formData.emergencyPhone || 'N/A',
          knownAllergies: formData.allergies.trim() || 'None',
          medicalHistory: formData.medicalHistory.trim() || 'None',
          currentMedications: formData.currentMedications.trim() || 'None',
        };
        const res = await client.put('/api/patients', payload);
        if (res.data?.success) {
          toast.success('Patient updated successfully.');
          setShowFormModal(false);
          loadPatients();
        } else {
          toast.error(res.data?.message || 'Update failed.');
        }
      } else {
        const payload = {
          adminUserID: adminUserId,
          patient: {
            fullName: formData.fullName.trim(),
            age: parseInt(formData.age, 10),
            gender: formData.gender,
            mobileNumber: formData.mobileNumber.trim(),
            email: formData.emailId.trim(),
            password: formData.password,
            addressLine: formData.address.trim(),
            landmark: formData.landmark.trim() || null,
            districtID: idDistrict,
            pinCode: formData.pincode,
            emergencyContactName: formData.emergencyName.trim() || 'N/A',
            emergencyContactPhoneNumber: formData.emergencyPhone || 'N/A',
            knownAllergies: formData.allergies.trim() || 'None',
            medicalHistory: formData.medicalHistory.trim() || 'None',
            currentMedications: formData.currentMedications.trim() || 'None',
            isEmailVerified: true,
          },
        };
        const res = await client.post('/api/admin/patients', payload);
        if (res.data?.success) {
          toast.success('Patient created successfully.');
          setShowFormModal(false);
          loadPatients();
        } else {
          toast.error(res.data?.message || 'Create failed.');
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—';

  return (
    <>
      <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
        <h2 className="dashboard-title mb-0">Patient List</h2>
        <div className="d-block d-md-flex justify-content-between align-items-center">
          <div className="btn-group me-md-3 mb-2 mb-md-0" role="group" aria-label="View Toggle">
            <button
              type="button"
              className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <FaList />
            </button>
            <button
              type="button"
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <FaTh />
            </button>
          </div>
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            <FaPlus className="me-1" /> Add Patient
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="row align-items-end">
          <div className="col-md-4 mb-3 mb-md-0">
            <label htmlFor="patientSearch" className="form-label">
              Search
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <FaSearch />
              </span>
              <input
                type="text"
                className="form-control"
                id="patientSearch"
                placeholder="Name, email, mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4 mb-3 mb-md-0">
            <label htmlFor="districtFilter" className="form-label">
              District
            </label>
            <select
              className="form-select"
              id="districtFilter"
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.idDistrict} value={d.idDistrict}>
                  {d.districtName}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 mb-3 mb-md-0">
            <label htmlFor="genderFilter" className="form-label">
              Gender
            </label>
            <select
              className="form-select"
              id="genderFilter"
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
            >
              <option value="">All Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {viewMode === 'table' && (
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th scope="col">Sl#</th>
                  <th scope="col">Patient Name</th>
                  <th scope="col">Age/Gender</th>
                  <th scope="col">Mobile</th>
                  <th scope="col">Address</th>
                  <th scope="col">District</th>
                  <th scope="col">PIN</th>
                  <th scope="col">Registered</th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && patients.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center">
                      No patients found
                    </td>
                  </tr>
                )}
                {!loading &&
                  patients.map((p, idx) => (
                    <tr key={p.idPatient}>
                      <td>{idx + 1}</td>
                      <td className="fw-bold">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary text-decoration-none"
                          onClick={() => openView(p)}
                        >
                          {p.fullName}
                        </button>
                      </td>
                      <td>
                        {p.age} / {p.gender}
                      </td>
                      <td>{p.mobileNumber}</td>
                      <td>{p.addressLine || '-'}</td>
                      <td>{p.districtName || '-'}</td>
                      <td>{p.pinCode || '-'}</td>
                      <td>{formatDate(p.createdAt)}</td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => openView(p)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => openEdit(p)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted small mb-0">
            Showing {patients.length} patient{patients.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      )}

      {viewMode === 'grid' && (
        <>
          {loading && (
            <div className="card">
              <div className="card-body text-center py-5">Loading...</div>
            </div>
          )}
          {!loading && patients.length === 0 && (
            <div className="card">
              <div className="card-body text-center py-5">
                <p className="text-muted mb-0">No patients found</p>
              </div>
            </div>
          )}
          {!loading && patients.length > 0 && (
            <>
              <div className="row g-3">
                {patients.map((p) => (
                  <div key={p.idPatient} className="col-12 col-md-6 col-lg-4">
                    <div className="card h-100 opportunity-card modern-card">
                      <div className="card-body d-flex flex-column p-0">
                        <div className="card-header-section">
                          <h5 className="card-position-title">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-dark text-decoration-none"
                              onClick={() => openView(p)}
                            >
                              {p.fullName}
                            </button>
                          </h5>
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary">
                              {p.age} / {p.gender}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link btn-outline-primary p-1 px-2"
                              title="Edit patient"
                              onClick={() => openEdit(p)}
                            >
                              <BiPencil />
                            </button>
                            <button
                              type="button"
                              className="btn btn-link btn-outline-primary p-1 px-2"
                              title="View patient"
                              onClick={() => openView(p)}
                            >
                              <FaEye />
                            </button>
                          </div>
                        </div>

                        <div className="card-info-section">
                          <div className="info-item">
                            <div className="info-icon hospital-icon">
                              <FaCalendarAlt />
                            </div>
                            <div className="info-content">
                              <div className="info-label">Registered</div>
                              <div className="info-value">{formatDate(p.createdAt)}</div>
                            </div>
                          </div>

                          <div className="info-item">
                            <div className="info-icon location-icon">
                              <FaMapMarkerAlt />
                            </div>
                            <div className="info-content">
                              <div className="info-label">Address</div>
                              <div className="info-value">
                                {p.addressLine || '—'}
                                <br />
                                <small className="text-muted">
                                  {p.districtName || '—'} · PIN: {p.pinCode || '—'}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-divider"></div>

                        <div className="card-details-section">
                          <div className="detail-item">
                            <FaPhone className="detail-icon" />
                            <div className="detail-content">
                              <span className="detail-label">Mobile</span>
                              <span className="detail-value">{p.mobileNumber || '—'}</span>
                            </div>
                          </div>

                          <div className="detail-item">
                            <FaUser className="detail-icon" />
                            <div className="detail-content">
                              <span className="detail-label">Email</span>
                              <span className="detail-value text-break">{p.email || '—'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-muted small mt-3 mb-0">
                Showing {patients.length} patient{patients.length !== 1 ? 's' : ''}
              </p>
            </>
          )}
        </>
      )}

      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Patient Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPatient && (
            <>
              <div className="reg-form-section">
                <div className="reg-section-title">
                  <span className="reg-section-num">1</span>
                  <span>Personal Information</span>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <strong>Name:</strong> {selectedPatient.fullName}
                  </div>
                  <div className="col-md-6">
                    <strong>Age / Gender:</strong> {selectedPatient.age} / {selectedPatient.gender}
                  </div>
                </div>
              </div>
              <div className="reg-form-section">
                <div className="reg-section-title">
                  <span className="reg-section-num">2</span>
                  <span>Address Details</span>
                </div>
                <div className="row g-2">
                  <div className="col-12">
                    <strong>Address:</strong> {displayValue(selectedPatient.addressLine)}
                  </div>
                  <div className="col-md-6">
                    <strong>Landmark:</strong> {displayValue(selectedPatient.landmark)}
                  </div>
                  <div className="col-md-6">
                    <strong>PIN / District:</strong> {displayValue(selectedPatient.pinCode)} /{' '}
                    {displayValue(selectedPatient.districtName)}
                  </div>
                </div>
              </div>
              <div className="reg-form-section">
                <div className="reg-section-title">
                  <span className="reg-section-num">3</span>
                  <span>Medical Details</span>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <strong>Emergency:</strong>{' '}
                    {displayValue(selectedPatient.emergencyContactName)} —{' '}
                    {displayValue(selectedPatient.emergencyContactPhoneNumber)}
                  </div>
                  <div className="col-12">
                    <strong>Allergies:</strong> {displayValue(selectedPatient.knownAllergies)}
                  </div>
                  <div className="col-12">
                    <strong>Medical History:</strong>{' '}
                    {displayValue(selectedPatient.medicalHistory)}
                  </div>
                  <div className="col-12">
                    <strong>Current Medications:</strong>{' '}
                    {displayValue(selectedPatient.currentMedications)}
                  </div>
                </div>
              </div>
              <div className="reg-form-section mb-0">
                <div className="reg-section-title">
                  <span className="reg-section-num">4</span>
                  <span>Contact</span>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <strong>Mobile:</strong> {selectedPatient.mobileNumber}
                  </div>
                  <div className="col-md-6">
                    <strong>Email:</strong> {displayValue(selectedPatient.email)}
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        centered
        size="xl"
        scrollable
        dialogClassName="admin-form-modal-dialog"
        contentClassName="admin-form-modal-content"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Patient' : 'Add Patient'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="admin-form-modal-body">
          <form id="admin-patient-form" className="login-form admin-patient-form" onSubmit={handleSubmit}>
            <p className="text-muted small mb-3">
              {isEditMode
                ? 'Update patient details using the same fields as patient registration.'
                : 'Register a new patient on behalf of the admin (email auto-verified, no OTP).'}
            </p>
            <PatientRegistrationFormFields
              variant={isEditMode ? 'admin-edit' : 'admin-add'}
              formData={formData}
              onChange={handleChange}
              onPincodeBlur={validatePincode}
              locationName={locationName}
              pincodeError={pincodeError}
              isValidatingPincode={isValidatingPincode}
              mobileError={mobileError}
              emailError={emailError}
              isValidatingContact={isValidatingContact}
              onMobileBlur={() => validateContact('mobile')}
              onEmailBlur={() => validateContact('email')}
              showPassword={showPassword}
              showReenterPassword={showReenterPassword}
              onToggleShowPassword={() => setShowPassword((v) => !v)}
              onToggleShowReenterPassword={() => setShowReenterPassword((v) => !v)}
              passwordsMatch={passwordsMatch}
            />
          </form>
        </Modal.Body>
        <Modal.Footer className="admin-form-modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowFormModal(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="admin-patient-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update Patient' : 'Register Patient'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AdminPatients;
